import api from "./api";

/**
 * Reports — the v2 engine (catalogue, run, drilldown, export, schedules) and
 * the one legacy report call still in use.
 *
 * Every endpoint takes the same three controls, so they are built in one place:
 * a date range, a branch filter, and nothing else. The API applies its own
 * permission scope on top — a manager asking for a branch they do not run gets
 * their own figures back, not that branch's — so this never has to reason about
 * who may see what.
 */

/** Controls -> query string, shared by every report and its export. */
const toParams = (filters = {}) => {
  const params = {};

  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.branch) params.branch = filters.branch;
  // Every money figure is for one currency; the API picks the reader's main
  // one when this is empty and says which in `meta.currency`.
  if (filters.currency) params.currency = filters.currency;

  return params;
};

/** Save a blob response under the server's filename. */
const saveBlob = (response, fallback) => {
  const disposition = response.headers["content-disposition"] ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const name = match ? match[1] : fallback;

  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: response.headers["content-type"] })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return name;
};

/**
 * One of the legacy reports (`loyalty`, `revenue`, …) — still read by the
 * dashboard's loyalty tile. The Reports page uses the v2 engine below.
 */
export const fetchReport = async (report, filters = {}) => {
  const { data } = await api.get(`/business/reports/${report}`, { params: toParams(filters) });

  return { report: data.data, meta: data.meta };
};

/* -------------------------------------------------------------------------
 * Reports v2 (spec 01). Every number comes from the engine; the portal never
 * adds money up itself. Filters: period | from/to, branches (array), currency
 * ("all" for one panel per currency), compare, group_by, refund_view.
 * ---------------------------------------------------------------------- */

const v2Params = (filters = {}) => {
  const params = { ...filters };

  if (Array.isArray(params.branches)) {
    params.branches = params.branches.join(",");
  }

  Object.keys(params).forEach((key) => {
    if (params[key] === "" || params[key] === null || params[key] === undefined) delete params[key];
  });

  return params;
};

/** GET /business/reports — categories, reports (with availability) and the KPI dictionary. */
export const fetchReportCatalogue = async () => {
  const { data } = await api.get("/business/reports");

  return data.data;
};

/** GET /business/reports/filter-options — only the branches/currencies the reader may reach. */
export const fetchReportFilterOptions = async () => {
  const { data } = await api.get("/business/reports/filter-options");

  return data.data;
};

/** GET /business/reports/{key} — meta, kpis, charts, table (one page). */
export const runReport = async (key, filters = {}, { page, perPage, sort, direction, refresh } = {}) => {
  const { data } = await api.get(`/business/reports/${key}`, {
    params: {
      ...v2Params(filters),
      ...(page ? { page } : {}),
      ...(perPage ? { per_page: perPage } : {}),
      ...(sort ? { sort, direction: direction ?? "asc" } : {}),
      ...(refresh ? { refresh: 1 } : {}),
    },
  });

  return data.data;
};

/** GET /business/reports/{key}/drilldown?point=day:2026-09-03 */
export const drilldownReport = async (key, point, filters = {}, page = 1) => {
  const { data } = await api.get(`/business/reports/${key}/drilldown`, {
    params: { ...v2Params(filters), point, page },
  });

  return data.data;
};

/**
 * POST /business/reports/{key}/export — the file now (≤ 2,000 rows), or a
 * queued export `{ queued: true, export }` announced by an RPT-01
 * notification. `pdf` is a print-ready HTML page, opened in a new tab.
 */
export const exportReportV2 = async (key, format, filters = {}) => {
  const response = await api.post(
    `/business/reports/${key}/export`,
    { format, filters: v2Params(filters) },
    { responseType: "blob", validateStatus: (status) => status === 200 || status === 202 }
  );

  if (response.status === 202) {
    const body = JSON.parse(await response.data.text());

    return { queued: true, export: body.data.export };
  }

  if (format === "pdf") {
    const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);

    return { queued: false, filename: null };
  }

  return { queued: false, filename: saveBlob(response, `${key}.${format}`) };
};

/** GET /business/reports/exports/{id} — a queued export's status. */
export const fetchReportExport = async (id) => {
  const { data } = await api.get(`/business/reports/exports/${id}`);

  return data.data.export;
};

/** GET /business/reports/exports/{id}/download */
export const downloadReportExport = async (id) => {
  const response = await api.get(`/business/reports/exports/${id}/download`, { responseType: "blob" });

  return saveBlob(response, `report-${id}`);
};

/** Saved / scheduled reports — the creator's own. */
export const fetchReportSchedules = async (reportKey) => {
  const { data } = await api.get("/business/reports/schedules", {
    params: reportKey ? { report_key: reportKey } : {},
  });

  return data.data.schedules;
};

export const createReportSchedule = async (payload) => {
  const { data } = await api.post("/business/reports/schedules", payload);

  return data.data.schedule;
};

export const updateReportSchedule = async (id, payload) => {
  const { data } = await api.put(`/business/reports/schedules/${id}`, payload);

  return data.data.schedule;
};

export const deleteReportSchedule = async (id) => {
  await api.delete(`/business/reports/schedules/${id}`);
};
